import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Link,
  Chip,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Phone as PhoneIcon,
  ShoppingCart as SageIcon,
  Link as LinkIcon,
  Dashboard as DashboardIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { manufacturingAPI } from '../../services/api';
import CodeMappingDialog from '../../components/manufacturing/CodeMappingDialog';
import usePageTitle from '../../hooks/usePageTitle';

const steps = ['Upload File', 'Validate & Map', 'Import Complete'];

const SageImport = () => {
  const navigate = useNavigate();
  
  // File upload state
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  
  // Mapping state
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [missingCodes, setMissingCodes] = useState([]);
  
  // Results state
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  usePageTitle('Sage Import');

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const fileType = selectedFile.name.split('.').pop().toLowerCase();
    if (fileType !== 'csv') {
      toast.error('Please upload a CSV file from Sage 50');
      return;
    }

    setFile(selectedFile);
    setResults(null);
    setError(null);
    setActiveStep(0);
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setImporting(true);
    setError(null);
    setResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await manufacturingAPI.customerOrders.importSageOrders(formData);

      // Check if mapping is required
      if (response.data.requires_mapping) {
        setMissingCodes(response.data.missing_codes);
        setMappingDialogOpen(true);
        setActiveStep(1);
        toast.info(`Found ${response.data.missing_codes.length} codes that need mapping`);
        return;
      }

      // Check if any orders are blocked (already in production or completed)
      if (response.data.has_blocked_orders) {
        setError({
          type: 'blocked_orders',
          message: response.data.message,
          blockedOrders: response.data.blocked_orders || [],
        });
        setActiveStep(1);
        toast.warning(
          `${response.data.blocked_orders?.length || 0} order(s) blocked - already in production or completed`
        );
        return;
      }
      
      // Import successful
      const summary = response.data.summary;
      setResults(summary);
      setActiveStep(2);
      
      // Build success message
      const messageParts = [];
      if (summary.customers_created > 0) messageParts.push(`${summary.customers_created} customers`);
      if (summary.orders_created > 0) messageParts.push(`${summary.orders_created} orders`);
      if (summary.lines_created > 0) messageParts.push(`${summary.lines_created} lines`);
      if (summary.special_codes_created > 0) messageParts.push(`${summary.special_codes_created} special codes`);
      
      toast.success(
        <div>
          <strong>Import Complete!</strong>
          <div style={{marginTop: '8px'}}>
            {messageParts.length > 0 ? `Created: ${messageParts.join(', ')}` : 'No changes made'}
          </div>
        </div>,
        { autoClose: 8000 }
      );
      
    } catch (err) {
      console.error('Import error:', err);
      
      const errorData = err.response?.data;
      
      // Check if this is a validation error (field length issues)
      if (errorData?.validation_errors) {
        setError({
          type: 'validation',
          message: errorData.error,
          validationErrors: errorData.validation_errors,
          details: errorData.details || []
        });
        setActiveStep(1);
        toast.error(`Validation failed: ${errorData.validation_errors.length} field(s) too long`);
      } else {
        setError({
          type: 'general',
          message: errorData?.error || err.message || 'Import failed',
          details: errorData
        });
        toast.error(errorData?.error || 'Import failed');
      }
    } finally {
      setImporting(false);
    }
  };

  const handleMappingsCreated = async () => {
    setMappingDialogOpen(false);
    toast.success('Mappings saved! Re-running import...');
    
    // Automatically re-run the import
    await handleImport();
  };

  const resetImport = () => {
    setFile(null);
    setResults(null);
    setError(null);
    setActiveStep(0);
    setMissingCodes([]);
  };

  const copyErrorsToClipboard = () => {
    if (error?.validationErrors) {
      const text = error.validationErrors.join('\n');
      navigator.clipboard.writeText(text);
      toast.success('Errors copied to clipboard');
    }
  };

  const copyBlockedOrdersToClipboard = () => {
    if (error?.blockedOrders) {
      const text = error.blockedOrders
        .map(o => `${o.sage_order_number}\t${o.order_number}\t${o.customer_name}\t${o.status}`)
        .join('\n');
      navigator.clipboard.writeText(text);
      toast.success('Blocked orders copied to clipboard');
    }
  };

  const formatStatus = (status) => {
    if (!status) return '';
    return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <SageIcon sx={{ fontSize: 48, mr: 2, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Sage 50 Order Import
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Import customer orders from Sage 50 with automatic code mapping
          </Typography>
        </Box>
      </Box>

      {/* Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                error={index === 1 && (error?.type === 'validation' || error?.type === 'blocked_orders')}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Step 0: Upload */}
          {activeStep === 0 && !error && (
            <Paper sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Step 1: Upload Sage CSV File
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Export your sales orders from Sage 50 as CSV, then upload the file here.
                </Typography>
              </Alert>

              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Before You Import:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText 
                      primary="Export from Sage 50"
                      secondary="File -> Export -> Sales Orders -> Save as CSV"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><PhoneIcon fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText 
                      primary="BOOKIN codes will be extracted"
                      secondary="Phone numbers and special codes automatically captured"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><LinkIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText 
                      primary="Code mapping available"
                      secondary="Map Sage codes to your internal item codes if they differ"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><WarningIcon fontSize="small" color="warning" /></ListItemIcon>
                    <ListItemText 
                      primary="Existing orders will be updated"
                      secondary="Re-importing the same order will update it, not duplicate"
                    />
                  </ListItem>
                </List>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="sage-file-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="sage-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<UploadIcon />}
                    size="large"
                  >
                    {file ? file.name : 'Choose Sage CSV'}
                  </Button>
                </label>

                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleImport}
                  disabled={!file || importing}
                  startIcon={importing ? <CircularProgress size={20} /> : <SageIcon />}
                >
                  {importing ? 'Processing...' : 'Start Import'}
                </Button>

                {file && !importing && (
                  <Button
                    variant="text"
                    onClick={resetImport}
                  >
                    Clear
                  </Button>
                )}
              </Box>

              {file && !results && !error && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    <strong>File selected:</strong> {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Click "Start Import" to begin processing.
                  </Typography>
                </Alert>
              )}
            </Paper>
          )}

          {/* Validation Error Display */}
          {error?.type === 'validation' && (
            <Paper sx={{ p: 4 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Validation Failed
                </Typography>
                <Typography variant="body1">
                  {error.message}
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                  Fields that exceed maximum length ({error.validationErrors?.length || 0} issues):
                </Typography>
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={copyErrorsToClipboard}
                >
                  Copy All
                </Button>
              </Box>
              
              {/* Detailed Error Table */}
              {error.details && error.details.length > 0 && (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'error.light' }}>
                        <TableCell><strong>Location</strong></TableCell>
                        <TableCell><strong>Field</strong></TableCell>
                        <TableCell align="center"><strong>Length</strong></TableCell>
                        <TableCell align="center"><strong>Max</strong></TableCell>
                        <TableCell><strong>Value Preview</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {error.details.map((err, idx) => (
                        <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {err.context}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={err.field} 
                              size="small" 
                              color="error" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2" color="error" fontWeight="bold">
                              {err.actual_length}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">
                              {err.max_length}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                wordBreak: 'break-all',
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              "{err.value_preview}"
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Fallback: Simple List if no details */}
              {(!error.details || error.details.length === 0) && error.validationErrors && (
                <List sx={{ bgcolor: 'error.lighter', borderRadius: 1, mb: 3 }}>
                  {error.validationErrors.map((err, idx) => (
                    <ListItem key={idx} divider={idx < error.validationErrors.length - 1}>
                      <ListItemIcon>
                        <ErrorIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={err}
                        primaryTypographyProps={{ 
                          variant: 'body2',
                          fontFamily: 'monospace',
                          sx: { wordBreak: 'break-all' }
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  How to fix:
                </Typography>
                <Typography variant="body2" component="div">
                  <ol style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Open your CSV file in Excel or a text editor</li>
                    <li>Find the fields listed above (check the "Location" column for the order number)</li>
                    <li>Shorten the values to fit within the maximum length</li>
                    <li>Save the file and try importing again</li>
                  </ol>
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Common issues:</strong>
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                    <li><strong>Phone numbers:</strong> Max 50 characters - remove extra text or notes</li>
                    <li><strong>Postcodes:</strong> Max 20 characters - use standard format only</li>
                    <li><strong>Contact names:</strong> Max 200 characters - abbreviate if needed</li>
                    <li><strong>Customer reference:</strong> Max 50 characters</li>
                  </ul>
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={resetImport}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Re-select file
                    document.getElementById('sage-file-upload-retry')?.click();
                  }}
                >
                  Choose Different File
                </Button>
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="sage-file-upload-retry"
                  type="file"
                  onChange={(e) => {
                    handleFileSelect(e);
                    setError(null);
                  }}
                />
              </Box>
            </Paper>
          )}

          {/* Blocked Orders Display - orders already in production / completed */}
          {error?.type === 'blocked_orders' && (
            <Paper sx={{ p: 4 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Import Blocked: Orders Already In Production or Completed
                </Typography>
                <Typography variant="body1">
                  {error.message}
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                  Orders in your CSV that are already past 'confirmed' ({error.blockedOrders?.length || 0}):
                </Typography>
                <Button
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={copyBlockedOrdersToClipboard}
                >
                  Copy All
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'error.light' }}>
                      <TableCell><strong>Sage Order #</strong></TableCell>
                      <TableCell><strong>Order #</strong></TableCell>
                      <TableCell><strong>Customer</strong></TableCell>
                      <TableCell><strong>Current Status</strong></TableCell>
                      <TableCell><strong>Order Date</strong></TableCell>
                      <TableCell><strong>Completed</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {error.blockedOrders?.map((bo, idx) => (
                      <TableRow key={idx} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {bo.sage_order_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {bo.order_number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {bo.customer_name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={formatStatus(bo.status)}
                            size="small"
                            color={bo.status === 'cancelled' ? 'default' : 'warning'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {bo.order_date ? new Date(bo.order_date).toLocaleDateString('en-GB') : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {bo.completed_at ? new Date(bo.completed_at).toLocaleDateString('en-GB') : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Why are these blocked?
                </Typography>
                <Typography variant="body2">
                  These orders have already moved past 'confirmed' (production started, ready to ship,
                  shipped, completed, or cancelled). Re-importing them from Sage would wipe their order
                  lines and stock allocations and reset their status to 'new', which can cause duplicate
                  work orders to be generated.
                </Typography>
              </Alert>

              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  How to fix:
                </Typography>
                <Typography variant="body2" component="div">
                  <ol style={{ margin: 0, paddingLeft: 20 }}>
                    <li>Open your CSV in Excel or a text editor</li>
                    <li>Remove the rows for the order numbers listed above</li>
                    <li>Save the file and try importing again</li>
                  </ol>
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  If you genuinely need to re-process one of these orders, change its status back to
                  'new' or 'confirmed' from the Customer Orders page first.
                </Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={resetImport}
                >
                  Try Again
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    document.getElementById('sage-file-upload-blocked-retry')?.click();
                  }}
                >
                  Choose Different File
                </Button>
                <input
                  accept=".csv"
                  style={{ display: 'none' }}
                  id="sage-file-upload-blocked-retry"
                  type="file"
                  onChange={(e) => {
                    handleFileSelect(e);
                    setError(null);
                  }}
                />
              </Box>
            </Paper>
          )}

          {/* General Error Display */}
          {error?.type === 'general' && (
            <Paper sx={{ p: 4 }}>
              <Alert severity="error" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Import Failed
                </Typography>
                <Typography variant="body1">
                  {error.message}
                </Typography>
              </Alert>

              {error.details?.traceback && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Technical Details:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    component="pre"
                    sx={{ 
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      maxHeight: 200,
                      overflow: 'auto'
                    }}
                  >
                    {error.details.traceback}
                  </Typography>
                </Alert>
              )}

              <Button
                variant="contained"
                onClick={resetImport}
              >
                Try Again
              </Button>
            </Paper>
          )}

          {/* Step 1: Mapping (handled by dialog) - show waiting state */}
          {activeStep === 1 && !error && (
            <Paper sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Step 2: Map Product Codes
              </Typography>
              
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  Some product codes from your Sage file don't exist in the system. 
                  A mapping dialog will open to help you map them.
                </Typography>
              </Alert>

              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={60} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Waiting for code mappings...
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Step 2: Results */}
          {activeStep === 2 && results && (
            <Paper sx={{ p: 4 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6">Import Complete!</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {results.message || 'Orders imported successfully'}
                </Typography>
              </Alert>

              <Grid container spacing={2}>
                <Grid item xs={6} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="caption">
                        Customers
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {results.customers_created || 0}
                      </Typography>
                      <Typography variant="caption">created</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="caption">
                        Orders
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {results.orders_created || 0}
                      </Typography>
                      <Typography variant="caption">created</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="caption">
                        Orders
                      </Typography>
                      <Typography variant="h5" color="info.main">
                        {results.orders_updated || 0}
                      </Typography>
                      <Typography variant="caption">updated</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="caption">
                        Order Lines
                      </Typography>
                      <Typography variant="h5" color="secondary.main">
                        {results.lines_created || 0}
                      </Typography>
                      <Typography variant="caption">created</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="text.secondary" variant="caption">
                        Special Codes
                      </Typography>
                      <Typography variant="h5">
                        {results.special_codes_created || 0}
                      </Typography>
                      <Typography variant="caption">BOOKIN etc</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                {results.codes_used_via_mapping > 0 && (
                  <Grid item xs={6} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" variant="caption">
                          Mapped Codes
                        </Typography>
                        <Typography variant="h5" color="warning.main">
                          {results.codes_used_via_mapping}
                        </Typography>
                        <Typography variant="caption">used</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

              {results.errors && results.errors.length > 0 && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Warnings ({results.errors.length})
                  </Typography>
                  <List dense>
                    {results.errors.slice(0, 5).map((error, idx) => (
                      <ListItem key={idx} sx={{ py: 0 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <WarningIcon fontSize="small" color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={error}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  {results.errors.length > 5 && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      ...and {results.errors.length - 5} more
                    </Typography>
                  )}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => navigate('/manufacturing/customer-orders')}
                  startIcon={<DashboardIcon />}
                >
                  View Orders
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetImport}
                >
                  Import Another File
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Quick Links
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItem button onClick={() => navigate('/manufacturing/customer-orders')}>
                  <ListItemText primary="Customer Orders" />
                </ListItem>
                <ListItem button onClick={() => navigate('/settings')}>
                  <ListItemText primary="Special Code Types" />
                </ListItem>
                <ListItem button onClick={() => navigate('/manufacturing/sage-mappings')}>
                  <ListItemText primary="Code Mappings" />
                </ListItem>
                <ListItem button onClick={() => navigate('/manufacturing/items')}>
                  <ListItemText primary="Items Management" />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Field Limits
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Maximum characters allowed:
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="Phone numbers"
                    secondary="50 characters"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="Customer name"
                    secondary="200 characters"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="Customer reference"
                    secondary="50 characters"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="Postcode"
                    secondary="20 characters"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="Address lines"
                    secondary="200 characters each"
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Tips
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List dense>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText 
                    primary="Code Mapping"
                    secondary="Maps Sage codes to internal codes automatically"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText 
                    primary="Re-import Safe"
                    secondary="Re-importing updates orders, doesn't duplicate"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon fontSize="small" color="info" /></ListItemIcon>
                  <ListItemText 
                    primary="Auto-create"
                    secondary="Customers are created automatically if new"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Code Mapping Dialog */}
      <CodeMappingDialog
        open={mappingDialogOpen}
        onClose={() => {
          setMappingDialogOpen(false);
          setFile(null);
          setActiveStep(0);
        }}
        missingCodes={missingCodes}
        onMappingsCreated={handleMappingsCreated}
      />
    </Container>
  );
};

export default SageImport;